import { subscriberSelectors } from './../../subscription/subscriber/subscriber.selectors'
import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { BsModalService } from 'ngx-bootstrap/modal'
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs'
import {
  catchError,
  map,
  take,
  tap,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core'
import { Router } from '@angular/router'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { environment } from '../../../../environments/environment'
import {
  BulkAction,
  BulkActionEvent,
  CreateCourseSale
} from '../../../shared/interfaces'
import { locationActions } from '../../location/location.actions'
import { productSelectors } from '../../product/product.selectors'
import { productActions } from '../../product/product.actions'
import { paymentActions } from '../payment/payment.actions'
import { Sale, StatusCode } from '../sale'
import { SaleItem } from '../sale-item/sale-item'
import { saleItemActions } from '../sale-item/sale-item.actions'
import { saleItemSelectors } from '../sale-item/sale-item.selectors'
import { SaleItemService } from '../sale-item/sale-item.service'
import { SaleItemShowModalComponent } from '../sale-item/show-modal/show.component'
import { saleActions } from '../sale.actions'
import { saleSelectors } from '../sale.selectors'
import { normalizationActions } from 'src/app/shared/normalization/normalization.actions'
import { normalizeToSchema } from 'src/app/shared/normalization/normalization'
import { persistentUserRoleSelectors } from '../../role/user-role/persistent-user-role/persistent-user-role.selector'
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { memberSelectors } from '../../member/member.selectors'
import { Booking } from '../../booking/booking'
import { UtilityService } from 'src/app/core/utility.service'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { Product } from '../../product/product'
import { Environment } from 'src/environments/environment.model'
import { bookingSelectors } from '../../booking/booking.selectors'
import { courseSelectors } from '../../course/course.selectors'
import { Course } from '../../course/course'
import { TranslateService } from '@ngx-translate/core'
import { productGroupActions } from '../../product/product-group/product-group.actions'
import { productGroupSelectors } from '../../product/product-group/product-group.selectors'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { ProductGroupType } from '../../product/product-group/product-group'
import { answerActions } from '../../survey/answer/answer.actions'
import { saleItemSurveyDraftSelectors } from '../sale-item-survey-draft/sale-item-survey-draft.selectors'
import { SaleItemSurveyDraft } from '../sale-item-survey-draft/sale-item-survey-draft.model'
import { Member } from '../../member/member'
import { memberActions } from '../../member/member.actions'
import { organizationSelectors } from '../../organization/organization.selectors'
import { Organization } from '../../organization/organization'
import { participationActions } from '../../booking/participation/participation.actions'
import { participationSelectors } from '../../booking/participation/participation.selectors'
import { Participation } from '../../booking/participation/participation'
import moment from 'moment-mini'

const UNPAID_PARTICIPATION_FETCH_CHUNK_SIZE = 500

type Tabs = 'selectCustomer' | 'saleItems' | 'createPayment'
type SaleItemPage =
  | 'selectType'
  | 'booking'
  | 'course'
  | 'subscription'
  | 'multiDay'
  | 'other'
  | 'rental'
  | 'unpaid'

interface SaleContext {
  customerName: string
  customerCategory: number
  organizationName: string
}

interface UnpaidBookingRow {
  id: string
  isSerie: boolean
  serieId?: string
  booking?: Booking
  activity: string
  startDate?: string
  participations: Participation[]
  children?: UnpaidBookingRow[]
}

@UntilDestroy()
@Component({
  selector: 'sale-create',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class SaleCreateComponent implements OnInit, OnDestroy {
  public saleItemPartials$: Observable<Partial<SaleItem>[]>
  public saleItemSurveyDrafts$: Observable<SaleItemSurveyDraft[]>
  public staticAssetBaseUrl: string = environment.staticAssetBaseUrl
  public saleItemPartialsTotal$: Observable<any>
  public activeTab: Tabs = 'selectCustomer'
  public resetForm$: BehaviorSubject<boolean> = new BehaviorSubject(false)
  public salePartial$: Observable<Partial<Sale>>
  public triggerSubmitSubject$: Subject<boolean> = new BehaviorSubject(
    undefined
  )
  private customerFormValid: boolean = false
  public isAvailable: boolean = true
  public customerPersonId: number
  public customerCategory: number
  public organizationId: number
  public selectedCustomerPersonId: number
  public isLoading$: Observable<boolean>
  public productEntities$ = this.store.select(productSelectors.selectEntities)
  public isMultiDayEnabled = environment.moduleConfigs.saleModule.multiDayFlow
  public isOrderNumberEnable = environment.moduleConfigs.saleModule.orderNumber
  public accessRights = AccessRights
  public form: UntypedFormGroup
  public createSaleFromCourses: boolean = false
  public dataForCreateCourseSale: CreateCourseSale
  public bookingFromCourse$: Observable<Booking>
  public selectedLanguage$: Observable<string>
  public environment: Environment = environment
  public saleItemPage: SaleItemPage = 'selectType'
  public emptySaleItem = new SaleItem()
  public saleContext$: Observable<SaleContext>
  public unpaidBookingRows$: Observable<UnpaidBookingRow[]>
  public courseIsLoading$: Observable<boolean>
  public selectedUnpaidParticipationIds = new Set<number>()
  private unpaidPreselectCategoryByParticipationId: Record<number, number> = {}
  private unpaidPreselectExpectedParticipationIds = new Set<number>()
  public expandedSerieIds = new Set<string>()
  public unpaidSearchTerm$ = new BehaviorSubject<string>('')
  public unpaidStartDate$ = new BehaviorSubject<string>(null)
  public unpaidEndDate$ = new BehaviorSubject<string>(null)
  public unpaidMinStartDate: string
  public unpaidMaxEndDate: string
  private unpaidDateRangeInitialized: boolean = false

  @ViewChild('salesItemsPurchase') salesItemsPurchase!: ElementRef

  constructor(
    private store: Store<AppState>,
    private utilityService: UtilityService,
    private actions$: Actions,
    private router: Router,
    private notificationService: NotificationService,
    private modalService: BsModalService,
    private saleItemService: SaleItemService,
    private fb: UntypedFormBuilder,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )
    //control for create sale from course participants if sale create came from courses
    this.isLoading$ = this.store.select(saleSelectors.selectIsLoading)
    this.courseIsLoading$ = this.store.select(courseSelectors.selectIsLoading)
    this.salePartial$ = this.store.select(saleSelectors.selectPartial)
    this.saleItemPartials$ = this.store.select(saleItemSelectors.selectPartials)
    this.saleItemSurveyDrafts$ = this.store.select(
      saleItemSurveyDraftSelectors.selectAll
    )
    this.store.dispatch(
      productGroupActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            type: ProductGroupType.SLOT_LENGTH_PRICING_GROUP
          },
          join: { field: 'products' }
        })
      })
    )
    this.saleItemPartialsTotal$ = combineLatest([
      combineLatest([this.saleItemPartials$, this.salePartial$]),
      combineLatest([
        this.store.select(productSelectors.selectEntities),
        this.store.select(memberSelectors.selectEntities),
        this.store.select(subscriberSelectors.selectAll),
        this.store.select(bookingSelectors.selectEntities),
        this.store.select(courseSelectors.selectEntities),
        this.store.select(productGroupSelectors.selectAll)
      ])
    ]).pipe(
      map(
        ([
          [saleItems, salePartial],
          [
            productEntities,
            memberEntities,
            subscribers,
            bookingEntities,
            courseEntities,
            productGroups
          ]
        ]) => {
          const didChargeBasePriceResourceProductIds: string[] = []

          if (!saleItems?.length || !salePartial) {
            return 0
          }

          return saleItems.reduce(
            (total, saleItem: any) => {
              // base price calc start
              let basePrice = 0

              const product: Product =
                saleItem.params?.bookableLinkedProductId ||
                saleItem.params?.bookableProductId
                  ? productEntities[
                      saleItem.params.bookableLinkedProductId ??
                        saleItem.params.bookableProductId
                    ]
                  : null

              const basePriceKey = `${saleItem.params?.startDate}-${saleItem.params?.bookableProductId}`
              if (
                product?.id &&
                product &&
                product.allowAsLinkedProduct &&
                product.allowChooseCategory &&
                product.basePrice &&
                saleItem.params?.bookableProductId &&
                !didChargeBasePriceResourceProductIds.includes(basePriceKey)
              ) {
                didChargeBasePriceResourceProductIds.push(basePriceKey)
                basePrice = product.basePrice
              }
              // base price calc end
              const saleItemTotal = !(
                saleItem.total === null || saleItem.total === undefined
              )
                ? saleItem.total
                : Number(
                    this.saleItemService.getSaleItemPriceDetails(
                      saleItem,
                      productEntities[saleItem.productId],
                      productEntities,
                      memberEntities[salePartial.customerPersonId],
                      subscribers,
                      saleItem?.courseId
                        ? courseEntities[saleItem.courseId]
                        : null,
                      bookingEntities,
                      productGroups
                    ).total ?? 0
                  )

              return total + basePrice + saleItemTotal
            },

            0
          )
        }
      )
    )

    this.form = this.fb.group({
      internalNote: [null, []],
      orderNumber: [null, []]
    })

    this.saleContext$ = combineLatest([
      this.salePartial$,
      this.store.select(memberSelectors.selectEntities),
      this.store.select(organizationSelectors.selectEntities)
    ]).pipe(
      map(([salePartial, memberEntities, organizationEntities]) => {
        const member: Member = memberEntities[salePartial?.customerPersonId]
        const organization: Organization =
          organizationEntities[salePartial?.organizationId]

        return {
          customerName: member
            ? [member.firstName, member.lastName].filter(Boolean).join(' ')
            : '',
          customerCategory:
            salePartial?.customerCategory ?? member?.category ?? null,
          organizationName: organization?.description ?? ''
        }
      })
    )

    const unpaidCustomerBookingRows$ = combineLatest([
      this.store.select(participationSelectors.selectAll),
      this.store.select(bookingSelectors.selectEntities),
      this.store.select(productSelectors.selectEntities),
      this.salePartial$
    ]).pipe(
      map(([participations, bookingEntities, productEntities, salePartial]) =>
        this.getUnpaidBookingRows(
          participations,
          bookingEntities,
          productEntities,
          salePartial?.customerPersonId ?? this.selectedCustomerPersonId
        )
      )
    )

    this.unpaidBookingRows$ = combineLatest([
      unpaidCustomerBookingRows$,
      this.unpaidSearchTerm$,
      this.unpaidStartDate$,
      this.unpaidEndDate$
    ]).pipe(
      tap(([rows]) => this.initializeUnpaidDateRange(rows)),
      map(([rows, unpaidSearchTerm, unpaidStartDate, unpaidEndDate]) =>
        this.groupUnpaidBookingRows(
          this.filterUnpaidBookingRows(
            rows,
            unpaidSearchTerm,
            unpaidStartDate,
            unpaidEndDate
          )
        )
      )
    )

    this.applyUnpaidPreselectFromNavigation()

    this.store.dispatch(locationActions.fetchMany({}))
  }

  public setIsAvailable(value) {
    this.isAvailable = value
  }
  public onClickNext() {
    if (this.customerFormValid) {
      switch (this.activeTab) {
        case 'selectCustomer':
          this.activeTab = 'saleItems'
          this.triggerSubmit()
          break
        case 'saleItems':
          // payment creation is no longer done in sale create flow but optionally afterwards in sale show page
          break
      }
    } else {
      this.triggerSubmitSubject$.next(false)
    }
    //this.customerFormValid = false
  }

  public triggerSubmit() {
    this.triggerSubmitSubject$.next(true)
  }

  public onCustomerSubmit(form: Partial<Sale>): void {
    if (
      !this.selectedCustomerPersonId ||
      (this.selectedCustomerPersonId &&
        this.selectedCustomerPersonId != form?.customerPersonId)
    ) {
      this.store.dispatch(saleActions.resetPartial())

      this.store.dispatch(
        saleActions.createOnePartial({
          entity: {
            ...form,
            ...this.form.value,
            posId: this.utilityService.getPosId()
          }
        })
      )
    }

    this.customerFormValid = true
    this.selectedCustomerPersonId = form?.customerPersonId
    this.customerPersonId = form?.customerPersonId
    this.customerCategory = form?.customerCategory
    this.organizationId = form?.organizationId
    this.activeTab = 'saleItems'
  }

  public onClickAddSaleItem() {
    this.saleItemPage = 'other'
  }

  public onClickAddSubscription() {
    this.saleItemPage = 'subscription'
  }
  public onClickAddRental() {
    this.saleItemPage = 'rental'
  }
  public onClickAddBooking() {
    this.saleItemPage = 'booking'
  }

  public onClickAddCourse() {
    this.saleItemPage = 'course'
  }

  public onClickCourseRow(course: Course) {
    if (course?.productId) {
      this.store.dispatch(
        productActions.fetchOneById({
          id: course?.productId,
          params: RequestQueryBuilder.create({
            join: [{ field: 'linkedSubscriptions' }]
          })
        })
      )
    }

    this.store.dispatch(
      saleItemActions.createOnePartial({
        entity: {
          ...new SaleItem(),
          subscriptionId: null,
          courseId: course.id,
          productId: course.productId,
          quantity: 1,
          bookingId: null,
          customerCategory: this.customerCategory,
          params: {
            invitedMemberEmails: [],
            invitedGuests: [],
            invitedOthers: [],
            startDate: moment(course.startDate).toISOString(),
            endDate: moment(course.endDate).toISOString()
          }
        }
      })
    )

    this.saleItemPage = 'selectType'
  }

  public onClickBackButton() {
    switch (this.activeTab) {
      case 'saleItems':
        if (this.saleItemPage !== 'selectType') {
          this.saleItemPage = 'selectType'
          return
        }
        this.activeTab = 'selectCustomer'
        break
    }
  }

  public onBulkAction(bulkActionEvent: BulkActionEvent) {
    if (bulkActionEvent.action == BulkAction.CANCEL) {
      bulkActionEvent.items.forEach((i) => {
        this.store.dispatch(saleItemActions.removeOnePartial({ id: i.id }))
      })
    }
  }

  public onClickRow(saleItem: SaleItem): void {
    this.modalService.show(SaleItemShowModalComponent, {
      initialState: {
        title: 'general.edit',
        saleItem
      },
      class: 'modal-lg modal-dialog-scrollable'
    })
  }

  public onClickDelete(saleItem: SaleItem) {
    const message = this.translate.instant('saleModule.deleteSaleProduct')
    if (!confirm(message)) {
      return
    }

    this.store.dispatch(saleItemActions.removeOnePartial({ id: saleItem.id }))
  }

  public onClickFinishSale() {
    this.salePartial$
      .pipe(take(1), withLatestFrom(this.saleItemPartials$))
      .subscribe(([partialSale, partialSaleItems]) => {
        const saleItems = partialSaleItems.map(
          ({ id, ...saleItem }) => saleItem
        )

        const saleItemsToInsert = this.setSaleItems(saleItems)

        this.actions$
          .pipe(
            ofType(saleActions.upsertMany),
            untilDestroyed(this),
            take(1),
            timeout(30000),
            catchError((error) => of(null))
          )
          .subscribe((action) => {
            console.log('upsertSale', action?.entities, action?.type)
          })

        const pSale = { ...partialSale }

        delete pSale?.customerCategory
        this.store
          .select(persistentUserRoleSelectors.selectSelectedUserRole)
          .pipe(take(1))
          .subscribe((pUr) => {
            const siteId =
              pUr && pUr.siteId ? pUr.siteId : environment.defaultSiteId
            this.store.dispatch(
              saleActions.createOne({
                entity: {
                  ...pSale,
                  statusCode: StatusCode.DONE,
                  saleItems: [...saleItemsToInsert],
                  siteId: siteId,
                  posId: this.utilityService.getPosId()
                }
              })
            )
            const partial = Object.values(pSale)
            this.store.dispatch(
              normalizationActions.upsertMany({
                entities: normalizeToSchema({ sales: { ...partial } })
              })
            )
          })
      })

    this.actions$
      .pipe(
        ofType(saleActions.upsertMany),
        untilDestroyed(this),
        take(1),
        timeout(120000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        const sale = action.entities[0]
        const customerPersonId = this.selectedCustomerPersonId

        this.saleItemSurveyDrafts$.pipe(take(1)).subscribe((surveyDrafts) => {
          const answersToCreate = this.buildAnswersToCreate(
            surveyDrafts,
            sale.id,
            customerPersonId
          )

          if (answersToCreate.length) {
            this.store.dispatch(
              answerActions.createMany({
                entities: answersToCreate
              })
            )
          }

          this.notificationService.emit('alerts.success', 'success')
          this.store.dispatch(saleActions.resetPartial())
          this.store.dispatch(saleItemActions.resetPartials())
          this.store.dispatch(paymentActions.resetPartials())
          this.router.navigate(['/sales/' + sale.id])
        })
      })
  }

  private buildAnswersToCreate(
    surveyDrafts: SaleItemSurveyDraft[],
    saleId: number,
    memberId: number
  ) {
    const uniqueDrafts = surveyDrafts.filter(
      (draft, index, drafts) =>
        draft.isValid &&
        draft.answers?.length &&
        index ===
          drafts.findIndex(
            (candidate) =>
              candidate.bookingId === draft.bookingId &&
              candidate.surveyId === draft.surveyId
          )
    )

    return uniqueDrafts.flatMap((draft) =>
      draft.answers.map((answer) => ({
        ...answer,
        saleId: Number(saleId),
        bookingId: Number(draft.bookingId),
        memberId: Number(memberId)
      }))
    )
  }

  public ngOnDestroy() {
    this.store.dispatch(saleActions.resetPartial())
    this.store.dispatch(saleItemActions.resetPartials())
    this.store.dispatch(paymentActions.resetPartials())
    this.createSaleFromCourses = false
  }

  public onClickAddMultiSaleItem() {
    this.saleItemPage = 'multiDay'
  }

  public onClickAddUnpaidBookings() {
    this.saleItemPage = 'unpaid'
    this.selectedUnpaidParticipationIds.clear()
    this.unpaidPreselectCategoryByParticipationId = {}
    this.unpaidPreselectExpectedParticipationIds.clear()
    this.expandedSerieIds.clear()
    this.unpaidDateRangeInitialized = false
    this.unpaidMinStartDate = null
    this.unpaidMaxEndDate = null
    this.unpaidSearchTerm$.next('')
    this.unpaidStartDate$.next(null)
    this.unpaidEndDate$.next(null)

    const customerPersonId = this.selectedCustomerPersonId

    if (!customerPersonId) {
      return
    }

    this.store.dispatch(
      participationActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            memberId: customerPersonId,
            paid: false,
            amount: { [CondOperator.GREATER_THAN]: 0 }
          },
          join: [
            { field: 'booking' },
            { field: 'booking.product' },
            { field: 'booking.linkedProduct' }
          ]
        })
      })
    )
  }

  private applyUnpaidPreselectFromNavigation() {
    const navigationState =
      this.router.getCurrentNavigation()?.extras?.state ?? history.state
    const preselect = navigationState?.unpaidPreselect

    if (!preselect?.memberId || !preselect?.participationIds?.length) {
      return
    }

    this.selectedCustomerPersonId = preselect.memberId
    this.customerPersonId = preselect.memberId
    this.customerFormValid = true
    this.activeTab = 'saleItems'
    this.saleItemPage = 'unpaid'
    const billableParticipationIds =
      preselect.billableParticipationIds ?? preselect.participationIds
    this.selectedUnpaidParticipationIds = new Set(billableParticipationIds)
    this.unpaidPreselectExpectedParticipationIds = new Set(
      billableParticipationIds
    )
    this.unpaidPreselectCategoryByParticipationId =
      preselect.participationCategories ?? {}
    this.expandedSerieIds.clear()
    this.unpaidDateRangeInitialized = false
    this.unpaidMinStartDate = null
    this.unpaidMaxEndDate = null
    this.unpaidSearchTerm$.next('')
    this.unpaidStartDate$.next(null)
    this.unpaidEndDate$.next(null)

    this.store.dispatch(memberActions.fetchOneById({ id: preselect.memberId }))
    this.store.dispatch(
      saleActions.createOnePartial({
        entity: {
          customerPersonId: preselect.memberId,
          posId: this.utilityService.getPosId()
        }
      })
    )
    this.fetchUnpaidParticipationsByIds(preselect.participationIds)
  }

  private fetchUnpaidParticipationsByIds(participationIds: number[]) {
    const ids = Array.from(new Set(participationIds ?? []))

    for (
      let index = 0;
      index < ids.length;
      index += UNPAID_PARTICIPATION_FETCH_CHUNK_SIZE
    ) {
      const chunkIds = ids.slice(
        index,
        index + UNPAID_PARTICIPATION_FETCH_CHUNK_SIZE
      )
      const params = RequestQueryBuilder.create({
        search: {
          id: { [CondOperator.IN]: chunkIds }
        },
        join: [
          { field: 'booking' },
          { field: 'booking.product' },
          { field: 'booking.linkedProduct' }
        ]
      })
      params.setLimit(chunkIds.length)

      this.store.dispatch(participationActions.fetchMany({ params }))
    }
  }

  public onUnpaidSearch(term: string) {
    this.unpaidSearchTerm$.next(term)
  }

  public onUnpaidStartDateChange(date: string) {
    const startDate = date || null
    const endDate = this.unpaidEndDate$.value

    if (startDate && endDate && moment(startDate).isAfter(endDate, 'day')) {
      this.unpaidStartDate$.next(endDate)
      return
    }

    this.unpaidStartDate$.next(startDate)
  }

  public onUnpaidEndDateChange(date: string) {
    const endDate = date || null
    const startDate = this.unpaidStartDate$.value

    if (startDate && endDate && moment(endDate).isBefore(startDate, 'day')) {
      this.unpaidEndDate$.next(startDate)
      return
    }

    this.unpaidEndDate$.next(endDate)
  }

  private getUnpaidBookingRows(
    participations: Participation[],
    bookingEntities,
    productEntities,
    customerPersonId: number
  ): UnpaidBookingRow[] {
    const bookingsById = new Map<number, Participation[]>()

    participations
      .filter(
        (participation) =>
          (!this.unpaidPreselectExpectedParticipationIds.size ||
            this.unpaidPreselectExpectedParticipationIds.has(
              participation.id
            )) &&
          participation.memberId == customerPersonId &&
          participation.paid === false &&
          Number(participation.amount ?? 0) > 0
      )
      .forEach((participation) => {
        const existing = bookingsById.get(participation.bookingId) ?? []
        bookingsById.set(participation.bookingId, [...existing, participation])
      })

    return Array.from(bookingsById.entries()).map(
      ([bookingId, bookingParticipations]) => {
        const participationBooking = bookingParticipations.find(
          (participation) => participation.booking
        )?.booking
        const booking =
          bookingEntities[bookingId] ?? participationBooking ?? null
        const product =
          productEntities[booking?.linkedProductId] ??
          productEntities[booking?.productId] ??
          booking?.linkedProduct ??
          booking?.product

        return {
          id: `booking-${bookingId}`,
          isSerie: false,
          booking,
          activity: product?.description ?? booking?.description ?? '',
          startDate: booking?.startDate as string,
          participations: bookingParticipations
        }
      }
    )
  }

  private filterUnpaidBookingRows(
    rows: UnpaidBookingRow[],
    unpaidSearchTerm: string,
    unpaidStartDate: string,
    unpaidEndDate: string
  ): UnpaidBookingRow[] {
    const term = unpaidSearchTerm?.toLowerCase().trim()
    const startTime = unpaidStartDate
      ? moment(unpaidStartDate).startOf('day').valueOf()
      : null
    const endTime = unpaidEndDate
      ? moment(unpaidEndDate).endOf('day').valueOf()
      : null

    return rows
      .filter((row) => {
        const rowTime = row.startDate ? moment(row.startDate).valueOf() : null

        if (startTime && (!rowTime || rowTime < startTime)) {
          return false
        }

        if (endTime && (!rowTime || rowTime > endTime)) {
          return false
        }

        if (!term) {
          return true
        }

        return (
          row.activity?.toLowerCase().includes(term) ||
          String(row.booking?.id ?? '').includes(term)
        )
      })
      .sort((a, b) =>
        String(a.startDate ?? '').localeCompare(String(b.startDate ?? ''))
      )
  }

  private groupUnpaidBookingRows(
    bookingRows: UnpaidBookingRow[]
  ): UnpaidBookingRow[] {
    const rows: UnpaidBookingRow[] = []
    const seriesRows = new Map<string, UnpaidBookingRow[]>()

    bookingRows.forEach((row) => {
      const serieId = row.booking?.serieId

      if (!serieId) {
        rows.push(row)
        return
      }

      seriesRows.set(serieId, [...(seriesRows.get(serieId) ?? []), row])
    })

    seriesRows.forEach((children, serieId) => {
      if (children.length === 1) {
        rows.push(children[0])
        return
      }

      rows.push({
        id: `serie-${serieId}`,
        isSerie: true,
        serieId,
        activity: children[0]?.activity ?? '',
        participations: children.flatMap((child) => child.participations),
        children
      })
    })

    return rows.sort((a, b) => {
      const aDate = a.children?.[0]?.startDate ?? a.startDate ?? ''
      const bDate = b.children?.[0]?.startDate ?? b.startDate ?? ''

      return String(aDate).localeCompare(String(bDate))
    })
  }

  private initializeUnpaidDateRange(rows: UnpaidBookingRow[]) {
    if (this.unpaidDateRangeInitialized || this.saleItemPage !== 'unpaid') {
      return
    }

    if (this.unpaidPreselectExpectedParticipationIds.size) {
      const loadedParticipationIds = new Set(
        rows.flatMap((row) =>
          row.participations.map((participation) => participation.id)
        )
      )
      const allPreselectedParticipationsLoaded = Array.from(
        this.unpaidPreselectExpectedParticipationIds
      ).every((id) => loadedParticipationIds.has(id))

      if (!allPreselectedParticipationsLoaded) {
        return
      }
    }

    const dates = rows
      .map((row) => (row.startDate ? moment(row.startDate) : null))
      .filter((date) => date?.isValid())

    if (!dates.length) {
      return
    }

    this.unpaidDateRangeInitialized = true
    this.unpaidMinStartDate = moment.min(dates).format('YYYY-MM-DD')
    this.unpaidMaxEndDate = moment.max(dates).format('YYYY-MM-DD')
    this.unpaidStartDate$.next(this.unpaidMinStartDate)
  }

  public getUnpaidBookingRowCount(rows: UnpaidBookingRow[]): number {
    return rows?.reduce(
      (total, row) => total + (row.children?.length ?? 1),
      0
    )
  }

  public onClickUnpaidBookingRow(row: UnpaidBookingRow) {
    if (!row?.booking?.id) {
      return
    }

    this.router.navigate(['/bookings', row.booking.id])
  }

  public getUnpaidRowParticipationIds(row: UnpaidBookingRow): number[] {
    return row.participations.map((participation) => participation.id)
  }

  public isUnpaidRowSelected(row: UnpaidBookingRow): boolean {
    const ids = this.getUnpaidRowParticipationIds(row)

    return (
      ids.length > 0 &&
      ids.every((id) => this.selectedUnpaidParticipationIds.has(id))
    )
  }

  public areAllUnpaidRowsSelected(rows: UnpaidBookingRow[]): boolean {
    const ids = rows?.flatMap((row) => this.getUnpaidRowParticipationIds(row))

    return (
      ids?.length > 0 &&
      ids.every((id) => this.selectedUnpaidParticipationIds.has(id))
    )
  }

  public toggleAllUnpaidRows(rows: UnpaidBookingRow[], event: Event) {
    const checked = (event.target as HTMLInputElement).checked

    rows
      ?.flatMap((row) => this.getUnpaidRowParticipationIds(row))
      .forEach((id) => {
        if (checked) {
          this.selectedUnpaidParticipationIds.add(id)
        } else {
          this.selectedUnpaidParticipationIds.delete(id)
        }
      })
  }

  public toggleUnpaidRow(row: UnpaidBookingRow, event: Event) {
    const checked = (event.target as HTMLInputElement).checked

    this.getUnpaidRowParticipationIds(row).forEach((id) => {
      if (checked) {
        this.selectedUnpaidParticipationIds.add(id)
      } else {
        this.selectedUnpaidParticipationIds.delete(id)
      }
    })
  }

  public toggleUnpaidSerie(row: UnpaidBookingRow) {
    if (!row.serieId) {
      return
    }

    if (this.expandedSerieIds.has(row.serieId)) {
      this.expandedSerieIds.delete(row.serieId)
      return
    }

    this.expandedSerieIds.add(row.serieId)
  }

  public isUnpaidSerieExpanded(row: UnpaidBookingRow): boolean {
    return !!row.serieId && this.expandedSerieIds.has(row.serieId)
  }

  public onClickCreateUnpaidSale() {
    const selectedIds = Array.from(this.selectedUnpaidParticipationIds)

    if (!selectedIds.length) {
      this.notificationService.emit('alerts.noItemsSelected', 'warning')
      return
    }

    this.store.dispatch(
      participationActions.updateMany({
        entities: selectedIds.map((id) => {
          const customerCategory =
            this.unpaidPreselectCategoryByParticipationId[id]

          return customerCategory !== null && customerCategory !== undefined
            ? { id, customerCategory }
            : { id }
        }),
        action: BulkAction.CREATE_SALE,
        targetSaleStatus: StatusCode.DONE
      })
    )

    this.actions$
      .pipe(
        ofType(
          participationActions.updateManySuccess,
          participationActions.updateManyFailed
        ),
        take(1),
        timeout(120000),
        catchError(() => of(null))
      )
      .subscribe((action) => {
        if (!action) {
          this.notificationService.emit('alerts.warning', 'warning')
          return
        }

        if (action.type === participationActions.updateManyFailed.type) {
          this.notificationService.emit('alerts.warning', 'warning')
          return
        }

        this.notificationService.emit('alerts.success', 'success')
        this.store.dispatch(saleActions.resetPartial())
        this.store.dispatch(saleItemActions.resetPartials())
        this.store.dispatch(paymentActions.resetPartials())
        this.unpaidPreselectCategoryByParticipationId = {}
        this.unpaidPreselectExpectedParticipationIds.clear()

        if (action.createdSaleIds?.length) {
          this.router.navigate(['/sales/' + action.createdSaleIds[0]])
        }
      })
  }

  private setSaleItems(saleItems: SaleItem[]): SaleItem[] {
    if (!saleItems?.length) {
      return []
    }

    let saleItemsArray = []

    saleItems.forEach((saleItem) => {
      if (
        saleItem?.params?.[`invitedCategory${saleItem.customerCategory}`]
          ?.length
      ) {
        saleItemsArray.push({
          bookingId: saleItem.bookingId ?? null,
          productId: saleItem.productId,
          quantity: saleItem.quantity,
          customerCategory: saleItem.customerCategory,
          params: {
            bookableProductId: saleItem.params.bookableProductId,
            bookableLinkedProductId: saleItem.params.bookableLinkedProductId,
            memberInvitesAmount: 0,
            invitedGuestCount: 0,
            invitedMemberEmails: [],
            invitedGuests: [
              ...saleItem?.params?.[
                `invitedCategory${saleItem.customerCategory}`
              ]
            ],
            invitedOthers: [],
            invitedOtherCount: 0,
            showOtherInviteEmailControls: false,
            primaryPurchaseMessage: null,
            secondaryPurchaseMessage: null,
            endDate: saleItem.params.endDate,
            startDate: saleItem.params.startDate,
            amount: 0
          }
        })
      } else {
        saleItemsArray.push({ ...saleItem })
      }
    })

    return saleItemsArray
  }
  public hideFinishSaleButton$() {
    return combineLatest([
      this.saleItemPartials$,
      this.saleItemPartialsTotal$
    ]).pipe(
      take(1),
      map(([saleItemPartials, saleItemPartialsTotal]) => {
        return !saleItemPartials?.length
      })
    )
  }
}
